import { isDangerousWasteDeploymentUnit } from './deploymentCategory.js';

const UNDEFINED_CATEGORY = 'UNDEFINED';

/**
 * Whether the given unit is a display_unit (aggregation across unit_ranges).
 */
export function isDisplayUnit(unit) {
    return Boolean(
        unit &&
        Array.isArray(unit.unit_ranges) &&
        unit.unit_ranges.length > 0
    );
}

/**
 * Stable identifier for a unit or display_unit. Display units do not have a
 * single unit_id, so we derive a deterministic string from their ranges.
 */
export function getUnitIdentifier(unit) {
    if (!unit) return null;
    if (Array.isArray(unit.unit_ranges) && unit.unit_ranges.length > 0) {
        return unit.unit_ranges
            .map((r) => `${r.unit_id}-${r.start_date}-${r.end_date ?? 'current'}`)
            .join('|');
    }
    return unit.unit_id ?? null;
}

/**
 * Underlying unit_id appropriate for direct Firestore lookups. Falls back to
 * the first range for display units.
 */
export function getUnderlyingUnitId(unit) {
    if (!unit) return null;
    if (unit.unit_id) return unit.unit_id;
    return unit.unit_ranges?.[0]?.unit_id ?? null;
}

/**
 * Returns the union of the unit's classification class names, historical eval categories, custom
 * categories, and aris_custom_categories — always including UNDEFINED.
 *
 * Pure. Order: class_names, eval_categories, custom_categories, aris_custom_categories,
 * UNDEFINED (only appended once, at the end if missing).
 */
export function getAllCategories(unit) {
    if (!unit) return [];

    const seen = new Set();
    const categories = [];

    const append = (list) => {
        if (!Array.isArray(list)) return;
        for (const cat of list) {
            if (cat == null || seen.has(cat)) continue;
            seen.add(cat);
            categories.push(cat);
        }
    };

    append(unit.deployment_config?.classification?.class_names);
    append(unit.eval_categories);
    append(unit.custom_categories);
    append(unit.aris_custom_categories);

    if (!seen.has(UNDEFINED_CATEGORY)) {
        categories.push(UNDEFINED_CATEGORY);
    }
    return categories;
}

/**
 * Dangerous-waste units use eval object shapes (instruction, etc.) that aren't in the
 * configured class_names whitelist, so callers must skip whitelist filtering.
 */
export function shouldSkipEvalCategoryWhitelist(unit) {
    return isDangerousWasteDeploymentUnit(unit);
}
